/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import GameSetup from '../../../Game/GameSetup';
import RecallEntry, { RecallEntryData } from '../../../Game/RecallEntry';
import RecallManager from '../../../Game/RecallManager';
import ComponentBase from '../../ComponentBase';
import InjectableComponent from '../ComponentReference/InjectableComponent';
import { ContextKey } from '../Configurator/ContextKey';
import IPopupManager from './IPopupManager';

/** @hidden */
const property = cc._decorator.property;

/** Component used to handle logic specifically for the Recall page */
@cc._decorator.ccclass
export default class RecallPage extends ComponentBase
{
    /** A reference to the node containing the recall list UI. */
    @property({ type: cc.Node, visible: true })
    private _recallListNode: cc.Node = null;

    /** A reference to the prefab representing a [[RecallEntry]]. */
    @property({ type: cc.Prefab, visible: true })
    private _recallListEntry: cc.Prefab = null;

    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();
    /** @hidden */
    protected _popupManagerRef: InjectableComponent<IPopupManager> = new InjectableComponent();
    /** @hidden */
    protected _recallManagerRef: InjectableComponent<RecallManager> = new InjectableComponent();

    /** @hidden */
    private _recallEntries: cc.Node[] = [];
    /** @hidden */
    private _recallEntriesPool: cc.NodePool = null;
    /** @hidden */
    private _nodePoolConfigured: boolean = false;

    private _recallLayout: cc.Layout = null;

    /** @hidden */
    protected setContexts(): void
    {
        this._popupManagerRef.SetContext(ContextKey.POPUP_MANAGER);
        this._recallManagerRef.SetContext(RecallManager);
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        this.configureNodePool();
    }

    /** @hidden */
    protected onEnable(): void
    {
        this._popupManagerRef.WithValue(popupManager =>
            {
                if (!popupManager.HasUserOpened) { return; }
                this._recallManagerRef.WithValue(recallManager => recallManager.PopulateRecallData());
            });
    }

    public AddRecallEntries(recallEntryData: RecallEntryData[]): void
    {
        for (let data of recallEntryData)
        {
            let entry: cc.Node = this.getNextRecallRow();
            entry.getComponent(RecallEntry).InitializeWithData(data);

            this._recallEntries.push(entry);
            this._recallListNode.addChild(entry);
        }

        if (!this._recallLayout) { this._recallLayout = this._recallListNode.getComponent(cc.Layout); }
        this._recallLayout.updateLayout();
    }

    public ClearRecallData(): void
    {
        this._recallEntries.forEach(entry => this._recallEntriesPool.put(entry));
        this._recallEntries.length = 0;
    }

    private configureNodePool(): void
    {
        if (this._nodePoolConfigured) { return; }

        this._gameSetupRef.SetContext(GameSetup);

        let size: number = this._gameSetupRef.HasValue()
            ? this._gameSetupRef.Value.RecallHistory
            : 10;

        this._recallEntriesPool = new cc.NodePool();

        while (size-- > 0)
        {
            this._recallEntriesPool.put(cc.instantiate(this._recallListEntry));
        }

        this._nodePoolConfigured = true;
    }

    private getNextRecallRow(): cc.Node
    {
        if (!this._nodePoolConfigured) { this.configureNodePool(); }
        return this._recallEntriesPool.get() || cc.instantiate(this._recallListEntry);
    }
}
